import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';

const INTERACTIVE_SELECTOR = 'a, button, [role="button"], input, label, [data-cursor]';

/**
 * Minimal difference-blend dot cursor. Grows over interactive elements.
 * Desktop (fine pointer) only; disabled for reduced motion.
 */
const CustomCursor: React.FC = () => {
  const dotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const finePointer = window.matchMedia('(pointer: fine)').matches;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!finePointer || reducedMotion || !dotRef.current) return;

    document.documentElement.classList.add('custom-cursor');

    const dot = dotRef.current;
    const x = gsap.quickTo(dot, 'x', { duration: 0.25, ease: 'power3.out' });
    const y = gsap.quickTo(dot, 'y', { duration: 0.25, ease: 'power3.out' });

    let visible = false;

    const onMove = (e: MouseEvent) => {
      if (!visible) {
        gsap.to(dot, { opacity: 1, duration: 0.2 });
        visible = true;
      }
      x(e.clientX);
      y(e.clientY);
      const target = (e.target as HTMLElement)?.closest?.(INTERACTIVE_SELECTOR);
      dot.classList.toggle('is-hover', !!target);
    };

    const onLeave = () => {
      gsap.to(dot, { opacity: 0, duration: 0.2 });
      visible = false;
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', onLeave);

    return () => {
      document.documentElement.classList.remove('custom-cursor');
      window.removeEventListener('mousemove', onMove);
      document.documentElement.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return <div ref={dotRef} className='cursor-dot opacity-0' aria-hidden='true' />;
};

export default CustomCursor;
